/**
 * chronos-bulk-pricing.js v4.0
 *
 * v4.0: speed only. The 300 ms variant check pauses while the tab is hidden and
 *   runs once when the tab is shown again. Nothing else changed.
 *
 * chronos-bulk-pricing.js v3.9
 *
 * v3.9: customer-side clarity (needs chronos-bulk-pricing.liquid v2.4):
 *   1. "Save N%" beside each tier price.
 *   2. Order total for the quantity chosen: "50 × ₹13 = ₹650 (you save ₹100)".
 *      If the same variant is already in the cart the total covers all of it,
 *      because the discount is calculated on cart + this order.
 *   3. One line saying prices include GST and, when the product has add-ons,
 *      that add-ons are charged at their regular price.
 *   4. If the cart already holds a different bulk-priced product, the page says
 *      so BEFORE the click and the Order in Bulk button is disabled (the click
 *      still checks too).
 *   Markup, layout and the order flow are unchanged.
 *
 * v3.8: tiers that are not below the variant's CURRENT price (or not below the
 *   tier before them) are ignored on the page, using the same rule as the worker
 *   (v3.4). Before, a price cut left a stale tier on screen and the Order in Bulk
 *   button then failed at the worker. If no valid tier is left, the page behaves
 *   as if the variant had no bulk pricing. Markup and styling unchanged.
 *
 * Stage 2 Part A — "Order in Bulk" fixes (markup/styling unchanged):
 *   1. No longer clears the cart. The bulk line is ADDED to whatever is already
 *      there. If the same variant is already in the cart, the discount is
 *      calculated on (already in cart + this order) so tiers stay correct.
 *   2. Add-ons picked on the page now go to the cart with the main product
 *      (uses window.ChronosAddons from chronos-addons.js v8.8, so the rules are
 *      identical to normal Add to Cart, incl. replaces_main).
 *   3. Required add-on groups are enforced: the groups are highlighted, the page
 *      scrolls to them and nothing is changed.
 *   4. Stock: the whole order is added in ONE atomic /cart/add.js call. If the
 *      store can't supply it, Shopify's message is shown and the cart is left
 *      exactly as it was. Sold-out variants no longer show the bulk button.
 *   5. Only one bulk-priced product per cart is supported by the discount
 *      tracker; ordering a different bulk variant while one is in the cart is
 *      blocked with a clear message instead of silently dropping the first
 *      discount.
 *   Debug logging removed.
 */
(function () {
  'use strict';

  var WORKER_URL = 'https://chronos-proxy-worker.orange-frog-2d58.workers.dev';

  var dataEl   = document.getElementById('chronos-bulk-pricing-data');
  var wrap     = document.getElementById('chronos-bulk-pricing');
  var listEl   = document.getElementById('cbp-tiers');
  var buyBtn   = document.getElementById('cbp-buy-now-btn');
  var statusEl = document.getElementById('cbp-buy-now-status');
  var totalEl  = document.getElementById('cbp-total');
  var noteEl   = document.getElementById('cbp-note');
  var limitEl  = document.getElementById('cbp-limit');
  if (!dataEl || !wrap || !listEl || !buyBtn || !statusEl) return;

  var DATA;
  try { DATA = JSON.parse(dataEl.textContent); } catch (e) { return; }
  if (!DATA || !DATA.variants) return;

  var currentVariantId = String(DATA.current_variant_id || '');
  var _nativeHidden = null;

  function getBulkForm() {
    return wrap.closest('form[action="/cart/add"]');
  }

  function getNativeSubmitButtons() {
    var form = getBulkForm();
    return form
      ? Array.prototype.slice.call(form.querySelectorAll('button[type="submit"], input[type="submit"]'))
      : [];
  }

  function getQty() {
    var form   = getBulkForm();
    var inputs = form ? form.querySelectorAll('input[name="quantity"]') : [];
    var q = null;
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].offsetParent !== null) { q = inputs[i]; break; }
    }
    if (!q && inputs.length) q = inputs[0];
    var val = Math.max(1, parseInt((q && q.value) || '1', 10) || 1);
    return val;
  }

  function getVariantId() {
    var form    = getBulkForm();
    var idInput = form ? form.querySelector('input[name="id"]') : null;
    return idInput ? String(idInput.value) : currentVariantId;
  }

  function formatRupees(n) {
    return '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  }

  function _parseMeta(val) {
    if (!val) return [];
    if (Array.isArray(val)) return val.map(Number);
    try { return JSON.parse(val).map(Number); } catch (e) { return []; }
  }

  // Valid tiers only: a tier counts when its price is above 0 and lower than
  // the current price and every earlier valid tier (same rule as the worker).
  function validTiers(entry) {
    if (!entry) return [];
    var base = Number(entry.base_price);
    var qArr = _parseMeta(entry.quantities);
    var pArr = _parseMeta(entry.prices);
    var pairs = [];
    for (var i = 0; i < qArr.length; i++) pairs.push({ min: qArr[i], price: pArr[i] });
    pairs.sort(function (a, b) { return a.min - b.min; });
    var out = [], floor = base;
    pairs.forEach(function (t) {
      if (!(t.min >= 2) || !(t.price > 0) || !(t.price < floor)) return;
      out.push(t);
      floor = t.price;
    });
    return out;
  }

  function buildTierList(entry) {
    var tiers = [{ min: 1, price: entry.base_price }].concat(validTiers(entry));
    for (var j = 0; j < tiers.length; j++) {
      tiers[j].max  = (j < tiers.length - 1) ? (tiers[j + 1].min - 1) : Infinity;
      tiers[j].best = (entry.best_qty != null && Number(tiers[j].min) === Number(entry.best_qty));
      tiers[j].save = (j > 0 && Number(entry.base_price) > 0)
        ? Math.round((1 - tiers[j].price / Number(entry.base_price)) * 1000) / 10
        : 0;
    }
    return tiers;
  }

  function setNativeButtonsVisible(visible) {
    if (_nativeHidden === !visible) return;
    _nativeHidden = !visible;
    getNativeSubmitButtons().forEach(function (btn) {
      btn.style.display = visible ? '' : 'none';
    });
  }

  function resetBuyNowButton() {
    buyBtn.disabled    = false;
    buyBtn.textContent = 'Order in Bulk';
    statusEl.style.display = 'none';
    statusEl.className     = 'cbp-buy-now-status';
    statusEl.textContent   = '';
  }

  // ── Order total, notes and the one-bulk-product limit (v3.9) ──
  var LIMIT_MSG = 'Your cart already has a bulk-priced item. Check out or remove it first to order this one in bulk.';
  var _cart = null, _cartAt = 0, _cartPromise = null;

  function getCartCached() {
    if (_cart && Date.now() - _cartAt < 8000) return Promise.resolve(_cart);
    if (_cartPromise) return _cartPromise;
    _cartPromise = fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { _cart = c; _cartAt = Date.now(); _cartPromise = null; return c; })
      .catch(function () { _cartPromise = null; return null; });
    return _cartPromise;
  }

  function unitPriceFor(entry, q) {
    var price = Number(entry.base_price);
    validTiers(entry).forEach(function (t) { if (q >= t.min) price = t.price; });
    return price;
  }

  function hide(el) { if (el) el.style.display = 'none'; }

  function clearExtras() {
    hide(totalEl); hide(limitEl);
    if (noteEl) noteEl.textContent = '';
  }

  function refreshExtras() {
    var vid   = currentVariantId;
    var entry = DATA.variants[vid];
    var valid = validTiers(entry);
    if (!valid.length || (entry && entry.available === false)) { clearExtras(); return; }

    if (noteEl) {
      noteEl.textContent = 'Prices include GST.' +
        (document.getElementById('chronos-addons') ? ' Add-ons are charged at their regular price.' : '');
    }

    var qty = getQty();
    if (qty < valid[0].min) { hide(totalEl); hide(limitEl); return; }

    getCartCached().then(function (cart) {
      // The page moved on while the cart was loading — a newer call will draw it.
      if (busy || vid !== currentVariantId || qty !== getQty()) return;

      var track   = readTrack();
      var blocked = !!(cart && track && track.variant_id && String(track.variant_id) !== String(vid) &&
                       lineQty(cart, track.variant_id) > 0);
      if (limitEl) {
        limitEl.textContent   = blocked ? LIMIT_MSG : '';
        limitEl.style.display = blocked ? '' : 'none';
      }
      buyBtn.disabled = blocked;

      if (!totalEl) return;
      if (blocked) { hide(totalEl); return; }

      var inCart = cart ? lineQty(cart, vid) : 0;
      var total  = inCart + qty;
      var unit   = unitPriceFor(entry, total);
      var base   = Number(entry.base_price);
      var line   = Math.round(unit * total * 100) / 100;
      var saved  = Math.round((base - unit) * total * 100) / 100;
      var u      = DATA.unit ? ' ' + DATA.unit : '';
      totalEl.innerHTML = total + u + ' × ' + formatRupees(unit) + ' = ' + formatRupees(line) +
        (saved > 0 ? ' (you save ' + formatRupees(saved) + ')' : '') +
        (inCart > 0 ? '<small>Includes the ' + inCart + ' already in your cart.</small>' : '');
      totalEl.style.display = '';
    });
  }

  // Cart changed somewhere else (drawer, cart page script) — look again.
  ['cart:updated', 'cart:refresh', 'dispatch:cart-drawer:refresh'].forEach(function (evt) {
    document.addEventListener(evt, function () { _cartAt = 0; refreshExtras(); });
  });

  var _lastHasTiers = null;

  function render() {
    var entry    = DATA.variants[currentVariantId];
    // Sold-out variants: hide the bulk button so the theme's own "Sold out"
    // button shows instead.
    var hasTiers = validTiers(entry).length > 0 && !(entry && entry.available === false);

    if (!hasTiers) {
      if (_lastHasTiers !== false) {
        wrap.style.display    = 'none';
        buyBtn.style.display  = 'none';
        setNativeButtonsVisible(true);
        _lastHasTiers = false;
      }
      clearExtras();
      return;
    }

    wrap.style.display = '';
    var tiers = buildTierList(entry);
    var qty   = getQty();
    var firstTierMin = validTiers(entry)[0].min;
    var qualifies = qty >= firstTierMin;

    listEl.innerHTML = tiers.map(function (t) {
      var active = qty >= t.min && qty <= t.max;
      var label  = (t.max === Infinity)
        ? (t.min + '+ units')
        : (t.min + ' \u2013 ' + t.max + ' units');
      var badge  = t.best ? '<span class="cbp-badge">Top choice</span>' : '';
      return '<div class="cbp-tier-row' + (active ? ' cbp-active' : '') + '">' +
               '<span>' + label + badge + '</span>' +
               '<span class="cbp-tier-price">' + (t.save > 0 ? '<span class="cbp-save">Save ' + t.save + '%</span>' : '') +
                 formatRupees(t.price) + ' / unit</span>' +
             '</div>';
    }).join('');

    if (qualifies) {
      setNativeButtonsVisible(false);
      buyBtn.style.display = '';
      statusEl.style.display = 'none';
      statusEl.className = 'cbp-buy-now-status';
      if (!buyBtn.disabled) buyBtn.textContent = 'Order in Bulk';
    } else {
      setNativeButtonsVisible(true);
      buyBtn.style.display = 'none';
      var needed = firstTierMin - qty;
      statusEl.style.display = '';
      statusEl.className = 'cbp-buy-now-status cbp-status-nudge';
      statusEl.textContent = 'Add ' + needed + ' more unit' + (needed === 1 ? '' : 's') + ' to unlock bulk pricing';
    }
    _lastHasTiers = true;
    refreshExtras();
  }

  function setVariant(id) {
    var idStr = String(id);
    if (idStr === currentVariantId) return;
    currentVariantId = idStr;
    render();
  }

  // ── ORDER IN BULK ──
  var TRACK_KEY = 'chronos_bulk_discount';
  var busy = false;

  function showStatus(cls, msg) {
    statusEl.style.display = '';
    statusEl.className     = 'cbp-buy-now-status ' + cls;
    statusEl.textContent   = msg;
  }
  function fail(msg) {
    busy = false;
    buyBtn.disabled    = false;
    buyBtn.textContent = 'Order in Bulk';
    showStatus('cbp-status-error', msg);
  }
  function readTrack() {
    try { return JSON.parse(sessionStorage.getItem(TRACK_KEY) || 'null'); } catch (e) { return null; }
  }
  function writeTrack(v) {
    try {
      if (v) sessionStorage.setItem(TRACK_KEY, JSON.stringify(v));
      else sessionStorage.removeItem(TRACK_KEY);
    } catch (e) {}
  }
  function lineQty(cart, vid) {
    var n = 0;
    (cart.items || []).forEach(function (it) {
      if (String(it.variant_id) === String(vid)) n += it.quantity;
    });
    return n;
  }

  buyBtn.addEventListener('click', async function () {
    if (busy) return;
    var variantId = getVariantId();
    var quantity  = getQty();
    if (!variantId) return;

    busy = true;
    buyBtn.disabled    = true;
    buyBtn.textContent = 'Preparing your order…';
    showStatus('cbp-status-loading', 'Hang on, checking your order…');

    var prevTrack = readTrack();
    var tracked   = false;

    try {
      // 1. Add-ons / required groups — same rules as Add to Cart.
      var items = [{ id: parseInt(variantId, 10), quantity: quantity }];
      var mainIncluded = true;
      if (window.ChronosAddons && window.ChronosAddons.buildOrder) {
        var order = await window.ChronosAddons.buildOrder(variantId, quantity);
        if (!order.ok) return fail(order.error);
        items = order.items;
        mainIncluded = order.mainIncluded;
      }

      // 2. Current cart (read only — nothing is cleared).
      var cartResp = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!cartResp.ok) return fail('Could not read your cart. Please try again.');
      var cart = await cartResp.json();

      // A "replaces main" add-on means the base product isn't ordered, so there
      // is no bulk line to price: add the items normally.
      if (!mainIncluded) {
        var plain = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items }),
        });
        if (!plain.ok) {
          var pe = await plain.json().catch(function () { return {}; });
          return fail(pe.description || pe.message || 'Could not add to cart.');
        }
        window.location.href = '/cart';
        return;
      }

      // 3. Only one bulk-priced product per cart.
      if (prevTrack && prevTrack.variant_id && String(prevTrack.variant_id) !== String(variantId) &&
          lineQty(cart, prevTrack.variant_id) > 0) {
        return fail('Your cart already has a bulk-priced product. Please check out or remove it first, then order this one in bulk.');
      }

      // 4. Discount for the TOTAL of this variant that will be in the cart.
      var totalQty = lineQty(cart, variantId) + quantity;
      var wr = await fetch(WORKER_URL + '/public/bulk-discount', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ variant_id: variantId, quantity: totalQty }),
      });
      var wd = await wr.json().catch(function () { return null; });
      if (!wr.ok || !wd || !wd.discount_code) {
        return fail((wd && wd.error) || 'Could not calculate your bulk discount. Please try again.');
      }

      // 5. Track before adding (the cart-sync script watches /cart/add), then
      //    add everything in one atomic call: all lines or none.
      writeTrack({ variant_id: variantId, quantity: totalQty, discount_code: wd.discount_code });
      tracked = true;
      buyBtn.textContent = 'Adding to cart…';
      showStatus('cbp-status-loading', 'Adding to your cart…');

      var addResp = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify({ items: items }),
      });
      if (!addResp.ok) {
        var ae = await addResp.json().catch(function () { return {}; });
        writeTrack(prevTrack);
        tracked = false;
        return fail(ae.description || ae.message || 'Sorry, we could not add this to your cart. Stock may be limited.');
      }

      // 6. Apply the discount and go to the cart.
      showStatus('cbp-status-loading', 'Applying your discount…');
      try { await fetch('/discount/NONE', { redirect: 'manual' }); } catch (e) {}
      window.location.href = '/discount/' + encodeURIComponent(wd.discount_code) + '?redirect=/cart';
    } catch (err) {
      if (tracked) writeTrack(prevTrack);
      fail('Something went wrong — please try again, or contact us if it persists.');
    }
  });

  // Variant change detection
  document.addEventListener('on:variant:change', function (e) {
    var d = e.detail || {};
    var v = d.variant || (d.id ? d : null);
    if (v && v.id) setVariant(v.id);
  });
  document.addEventListener('variant:changed', function (e) {
    var d = e.detail || {};
    var v = d.variant || (d.id ? d : null);
    if (v && v.id) setVariant(v.id);
  });

  var _polledVid = '';
  function pollTick() {
    if (document.hidden) return;
    var form = getBulkForm();
    var inp  = form && form.querySelector('input[name="id"]');
    if (inp && inp.value && inp.value !== _polledVid) {
      _polledVid = inp.value;
      setVariant(inp.value);
    }
  }
  setInterval(pollTick, 300);
  document.addEventListener('visibilitychange', function () { if (!document.hidden) pollTick(); });

  document.addEventListener('input', function (e) {
    if (e.target && e.target.matches('input[name="quantity"]')) render();
  });
  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches('input[name="quantity"]')) render();
  });

  (function() {
    var s = document.createElement('style');
    s.textContent = '.cbp-status-nudge{color:#daa520;font-size:13px;text-align:center;margin-top:6px;}';
    document.head.appendChild(s);
  })();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', render);
  } else {
    render();
  }
})();